import React from "react";
import { useParams } from "react-router-dom";
import { LegalShell, LEGAL_ITEMS } from "@/pages/Legal";
import { Card, Button } from "@/components/ui";
import { useCanonical, MARKETING_BASE } from "@/lib/seo";
import { ExternalLink, ShieldCheck, Flag, MailOpen } from "lucide-react";

const TERMLY = {
  privacy: process.env.REACT_APP_TERMLY_PRIVACY_ID,
  terms: process.env.REACT_APP_TERMLY_TOS_ID,
  disclaimer: process.env.REACT_APP_TERMLY_DISCLAIMER_ID,
  "acceptable-use": process.env.REACT_APP_TERMLY_AUP_ID,
  cookies: process.env.REACT_APP_TERMLY_COOKIE_ID,
};
const CA_URL = process.env.REACT_APP_TERMLY_CA_URL;
const isUuid = (v) => typeof v === "string" && /^[0-9a-f-]{20,}$/i.test(v) && v.includes("-");

function TermlyDoc({ id }) {
  if (isUuid(id)) {
    return (
      <div className="bg-white border border-line rounded-xl overflow-hidden">
        <iframe
          title="policy"
          src={`https://app.termly.io/policy-viewer/policy.html?policyUUID=${id}`}
          className="w-full"
          style={{ height: "70vh", border: "none" }}
          data-testid="termly-iframe"
        />
      </div>
    );
  }
  return (
    <Card data-testid="termly-fallback">
      <p className="text-sm text-ink">
        This policy is hosted live on Termly. It will appear here once the Termly document ID is configured
        in the app settings.
      </p>
      <p className="text-sm text-subtle mt-3">
        In the meantime, view all policies at <span className="font-semibold text-ink">kinbridgehealth.com</span> or
        email <span className="font-semibold text-ink">privacy@kinbridgehealth.com</span>.
      </p>
    </Card>
  );
}

function California() {
  return (
    <div>
      <div className="inline-flex items-center gap-1.5 bg-mint-tint text-mint-dark text-xs font-bold rounded-full px-3 py-1"><Flag className="w-3.5 h-3.5" /> CCPA / CPRA</div>
      <h2 className="font-display text-2xl text-ink mt-4">Limit the use of my sensitive personal information</h2>
      <p className="text-[15px] text-ink mt-3 leading-relaxed">
        Under the California Consumer Privacy Act (CCPA), California residents have the right to limit the use of their
        sensitive personal information — including health, insurance, and precise-location data — to only what is
        necessary to deliver the services you asked for.
      </p>
      <p className="text-[15px] text-ink mt-3 leading-relaxed">
        KinBridge Health does not sell or share your sensitive personal information for cross-context behavioral
        advertising. Submitting the request below adds an extra layer of restriction to how our team and vendors may
        process your data.
      </p>
      {CA_URL && (
        <a href={CA_URL} target="_blank" rel="noopener noreferrer" data-testid="california-limit-btn">
          <Button className="mt-5"><ShieldCheck className="w-4 h-4" /> Limit use of my information <ExternalLink className="w-3.5 h-3.5" /></Button>
        </a>
      )}
      <p className="text-xs text-subtle mt-3">The link opens our Termly-hosted intake form in your browser.</p>
      <div className="border-t border-line my-6" />
      <h3 className="font-display text-lg text-ink">What &quot;sensitive personal information&quot; means</h3>
      <p className="text-sm text-ink mt-2 leading-relaxed">
        Under CPRA §1798.140(ae), it includes health records, medical conditions, insurance information, precise
        geolocation, biometric identifiers, and communications content. These are the categories KinBridge Health
        primarily handles on your behalf.
      </p>
      <p className="text-xs text-subtle mt-6">
        Questions? Email privacy@kinbridgehealth.com. We respond to CCPA requests within 45 days (a further 45-day
        extension is permitted when reasonably necessary).
      </p>
    </div>
  );
}

function Dsar() {
  return (
    <div>
      <div className="inline-flex items-center gap-1.5 bg-brand-tint text-brand-dark text-xs font-bold rounded-full px-3 py-1"><MailOpen className="w-3.5 h-3.5" /> DATA RIGHTS</div>
      <h2 className="font-display text-2xl text-ink mt-4">Submit a Data Access Request</h2>
      <p className="text-[15px] text-ink mt-3 leading-relaxed">
        You can ask us to access, export, correct, or delete the personal data we hold about you and your care circle.
        We verify your identity before acting on any request and respond within the timelines required by law.
      </p>
      <a href="mailto:privacy@kinbridgehealth.com?subject=Data%20Access%20Request" data-testid="dsar-email-btn">
        <Button className="mt-5"><MailOpen className="w-4 h-4" /> Email a request</Button>
      </a>
      {CA_URL && (
        <a href={CA_URL} target="_blank" rel="noopener noreferrer" className="block mt-3 text-sm text-brand font-semibold hover:underline" data-testid="dsar-form-link">
          Or use our online intake form →
        </a>
      )}
      <p className="text-xs text-subtle mt-6">
        Revocation and deletion are prospective and do not affect data already processed prior to your request.
      </p>
    </div>
  );
}

function Security() {
  const rows = [
    ["Zero-persistence audio", "Ambient visit recordings are transcribed in-memory and discarded — only the transcript and structured summary are saved."],
    ["Encryption", "Documents are encrypted at rest in object storage; all traffic is encrypted in transit (TLS)."],
    ["BAA-covered AI", "Transcription and summarization run through providers under a Business Associate Agreement."],
    ["No PHI with payments", "Billing is handled by Stripe; no protected health information is shared with our payment processor."],
    ["Least-privilege access", "Care-circle roles (family, aide, viewer) scope what each member can see and do."],
  ];
  return (
    <div>
      <div className="inline-flex items-center gap-1.5 bg-mint-tint text-mint-dark text-xs font-bold rounded-full px-3 py-1"><ShieldCheck className="w-3.5 h-3.5" /> HIPAA-CONSCIOUS</div>
      <h2 className="font-display text-2xl text-ink mt-4">How we protect your health information</h2>
      <p className="text-[15px] text-ink mt-3 leading-relaxed">
        KinBridge Health is built to handle protected health information (PHI) responsibly. Our safeguards include:
      </p>
      <div className="mt-4 space-y-3">
        {rows.map(([t, b]) => (
          <Card key={t}>
            <div className="font-semibold text-ink">{t}</div>
            <p className="text-sm text-subtle mt-1 leading-relaxed">{b}</p>
          </Card>
        ))}
      </div>
      <p className="text-xs text-subtle mt-6">Security questions? Email security@kinbridgehealth.com.</p>
    </div>
  );
}

function Eula() {
  return (
    <Card>
      <p className="text-sm text-ink leading-relaxed">
        By using the KinBridge Health application you agree to our End User Licence Agreement, which governs your
        licence to use the app, acceptable-use expectations, and the limits of liability. The full EULA is hosted live
        via Termly.
      </p>
      <p className="text-sm text-subtle mt-3">
        View the current EULA at <span className="font-semibold text-ink">kinbridgehealth.com</span> or email
        <span className="font-semibold text-ink"> support@kinbridgehealth.com</span>.
      </p>
    </Card>
  );
}

export default function LegalDoc() {
  const { slug } = useParams();
  const meta = LEGAL_ITEMS.find((i) => i.slug === slug);
  const title = meta?.title || "Policy";
  useCanonical(`${MARKETING_BASE}/${slug}`, `${title} · KinBridge Health`);

  let body;
  if (slug === "california") body = <California />;
  else if (slug === "dsar") body = <Dsar />;
  else if (slug === "security") body = <Security />;
  else if (slug === "eula") body = <Eula />;
  else if (slug in TERMLY) body = <TermlyDoc id={TERMLY[slug]} />;
  else body = <Card>Policy not found.</Card>;

  return <LegalShell title={title} backTo="/legal" backLabel="Legal">{body}</LegalShell>;
}
